import React, { useEffect, useState, useRef } from "react";

import Plotly from "plotly.js-dist-min";
import * as locale from "utils/plotly-de";
import createPlotlyComponent from "react-plotly.js/factory";

import ToolTip from "components/ToolTip";
import moment from "moment";
import { useFormattedMessage } from "hooks";
import CustomFormatNumber from "components/CustomFormatNumber";

Plotly.register(locale);
const Plot = createPlotlyComponent(Plotly);

const messages = {
    noDataFound: "no_data_found",
    tokenprice: "token_price",
    indicativeTokenValue: "indicative_token_value",
    indicativeTokenValueITVTooltipContent: "unit_price_sublabel_tooltip",
    tokenMarketPriceTMPTooltipContent: "token_tmp_tooltip_content",
};

const PercentValueCom = ({ percentRate, gainPrice, fixed = 2, symbol = "€" }) => {
    return (
        <>
            <div className="d-flex align-end">
                <span
                    className={`text-12 fw-600 mr-2 ${
                        Math.sign(percentRate) === -1 || Math.sign(gainPrice) === -1 ? "danger-red-color" : "primary-color"
                    }`}
                >
                    {`${Math.sign(percentRate) === -1 || Math.sign(gainPrice) === -1 ? "" : "+"}`}
                    <CustomFormatNumber value={percentRate || gainPrice} symbol={symbol} fixed={fixed} />
                </span>
            </div>
        </>
    );
};

const PerformancePlotlyChart = ({ itvInfoClassName, itvValueClassName, performances, projectTitle, className = "", locale }) => {
    const gbRef = useRef();
    const [isMobileScreen, setMobileScreen] = useState(false);
    const [isSmMobileScreen, setSmMobileScreen] = useState(false);
    const [ITV, setITV] = useState({});
    const [TMP, setTMP] = useState({});
    const [innerWidth, setInnerWidth] = useState(400);
    const [innerHeight, setInnerHeight] = useState(360);
    const [yRange, setYRange] = useState([0.0, 1.0]);
    const [data, setData] = useState({
        ITV: [],
        TMP: [],
        tmpDates: [],
        itvDates: [],
        dates: [],
        itvApiDates: [],
        tmpApiDates: [],
    });
    const [display, setDisplay] = useState({ itvDate: new Date(), tmpDate: new Date(), ITV: 1.0, TMP: 1.0 });
    const { formatMessage } = useFormattedMessage();
    const [selectorOptions] = useState({
        bgcolor: "#FFFFFF",
        font: {
            family: "Montserrat, sans-serif",
            size: 12,
            color: "#9e9fa4",
        },
        width: 20,
        buttons: [
            {
                step: "all",
                label: "All",
                active: true,
            },
            {
                step: "day",
                stepmode: "backward",
                count: 180,
                label: "6-m",
            },
            {
                step: "day",
                stepmode: "backward",
                count: 30,
                label: "1-m",
            },
            {
                step: "day",
                stepmode: "backward",
                count: 7,
                label: "7-d",
            },
        ],
    });

    useEffect(() => {
        setInnerWidth(gbRef.current.offsetWidth);
        setData(arrangeData(performances));
        if (window.innerWidth < 768) {
            setInnerHeight(250);
        }
    }, [performances]);

    useEffect(() => {
        if (window.innerWidth < 375 && window.innerWidth > 357) {
            setMobileScreen(true);
        }
        if (window.innerWidth <= 356) {
            setSmMobileScreen(true);
        }
    }, [window, performances]);

    useEffect(() => {
        setYRange([
            Math.min(...data.ITV) - 0.05 < Math.min(...data.TMP) - 0.05
                ? Math.min(...data.ITV) - 0.05
                : Math.min(...data.TMP) - 0.05,
            Math.max(...data.TMP) + 0.05 > Math.max(...data.ITV) + 0.05
                ? Math.max(...data.TMP) + 0.05
                : Math.max(...data.ITV) + 0.05,
        ]);
        setDisplay({
            date: data.dates[data.dates.length - 1],
            itvDate: data.itvApiDates[data.itvApiDates.length - 1],
            tmpDate: data.tmpApiDates[data.tmpApiDates.length - 1],
            ITV: data.ITV[data.ITV.length - 1],
            TMP: data.TMP[data.TMP.length - 1],
        });
        setTMP({
            x: data.tmpDates,
            y: data.TMP,
            fill: "tonexty", // remove background color of graph line
            type: "scatter",
            mode: "lines+markers",
            name: "TMP",
            fillcolor: "transparent",
            marker: {
                symbol: "circle-dot",
                size: 3,
            },
            line: {
                shape: "linear",
                width: 1,
            },
            // text: data.liquidity,
            hoverinfo: "y+x",
        });
        setITV({
            x: data.itvDates,
            y: data.ITV,
            fill: "tozeroy", // remove background color of graph line
            type: "scatter",
            mode: "lines+markers",
            name: "ITV",
            fillcolor: "transparent",
            marker: {
                symbol: "circle-dot",
                size: 3,
            },
            line: {
                shape: "linear",
                width: 1,
            },
            hoverinfo: "y+x",
        });
    }, [data]);

    const arrangeData = (performances) => {
        const ITV = [],
            TMP = [],
            dates = [],
            itvDates = [],
            tmpDates = [],
            today = new Date(),
            itvApiDates = [],
            tmpApiDates = [];

        let lastDate = "",
            length = performances.length,
            itvDatesLength = itvDates.length,
            tmpDatesLength = tmpDates.length;

        for (let index = 0; index < length; index++) {
            dates.push(performances[index].ValuationDate.split("_")[0]);
            performances[index].TypePerformance === "IndicativeTokenValue" &&
                itvDates.push(performances[index].ValuationDate.split("_")[0]);
            performances[index].TypePerformance === "TokenMarketPrice" &&
                tmpDates.push(performances[index].ValuationDate.split("_")[0]);
            // itvApiDates and tmpApiDates
            performances[index].TypePerformance === "IndicativeTokenValue" &&
                itvApiDates.push(performances[index].ValuationDate.split("_")[0]);
            performances[index].TypePerformance === "TokenMarketPrice" &&
                tmpApiDates.push(performances[index].ValuationDate.split("_")[0]);

            performances[index].TypePerformance === "IndicativeTokenValue" &&
                ITV.push(parseFloat(performances[index].TokenPrice));
            performances[index].TypePerformance === "TokenMarketPrice" && TMP.push(parseFloat(performances[index].TokenPrice));
        }

        (length = dates.length), (lastDate = new Date(dates[length - 1]));
        (itvDatesLength = itvDates.length), (tmpDatesLength = tmpDates.length);

        if (lastDate < today) {
            dates.push(today);
            itvDates.push(moment(new Date(today)).format("YYYY-MM-DD"));
            tmpDates.push(moment(new Date(today)).format("YYYY-MM-DD"));
            ITV.push(ITV[itvDatesLength - 1]);
            TMP.push(TMP[tmpDatesLength - 1]);
        }
        return { dates, ITV, TMP, itvDates, tmpDates, itvApiDates, tmpApiDates };
    };

    return (
        <div className={`performance ${className ? className : "mt-20"}`}>
            <div className="performance-graph">
                <div className="performance-graph-inner flex justify-between flex-wrap mb-4">
                    <p className="text-14 fw-700 w-100 ">{projectTitle}</p>
                    <div className={`performance-project-title ${itvInfoClassName ? itvInfoClassName : "w-75"}`}>
                        <div className="performance-filter-btn flex justify-between pb-4 flex-wrap">
                            <div className="performance-init-text w-100">
                                <p className="text-12 mb-1 asset-value d-flex align-center mx-0">
                                    <span className="pit-fix-width">
                                        <span className="dot" />
                                        <span className="pit-text">ITV </span>-{" "}
                                        <CustomFormatNumber className="mx-1" value={display.ITV} symbol="€" fixed={2} />
                                    </span>
                                    <span className="another-grey-color">
                                        {formatMessage(messages.indicativeTokenValue)}
                                        <span className="ml-1">
                                            {locale == "de"
                                                ? moment(display.itvDate).format("DD.MM.YYYY")
                                                : moment(display.itvDate).format("YYYY-MM-DD")}
                                        </span>
                                    </span>
                                    <div
                                        className="question ml-1"
                                        data-tooltip-id="plotly-chart"
                                        data-place={isMobileScreen ? "right" : isSmMobileScreen ? "top" : "left"}
                                        data-tooltip-content={formatMessage(messages.indicativeTokenValueITVTooltipContent)}
                                    >
                                        ?
                                    </div>
                                </p>
                                <p className="text-12 mb-1 liquidity-reserves d-flex align-center mx-0">
                                    <span className="pit-fix-width">
                                        <span className="dot" />
                                        <span className="pit-text">TMP </span>-{" "}
                                        <CustomFormatNumber className="mx-1" value={display.TMP} symbol="€" fixed={2} />
                                    </span>
                                    <span className="another-grey-color">
                                        {formatMessage(messages.tokenprice)}
                                        <span className="ml-1">
                                            {locale == "de"
                                                ? moment(display.tmpDate).format("DD.MM.YYYY")
                                                : moment(display.tmpDate).format("YYYY-MM-DD")}
                                        </span>
                                    </span>
                                    <div
                                        className="question ml-1"
                                        data-tooltip-id="plotly-chart"
                                        data-place={isMobileScreen || isSmMobileScreen ? "right" : "left"}
                                        data-tooltip-content={formatMessage(messages.tokenMarketPriceTMPTooltipContent)}
                                    >
                                        ?
                                    </div>
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className={`performance-project-values ${itvValueClassName ? itvValueClassName : "w-25"}`}>
                        <div className="align-center d-flex justify-end">
                            <div
                                className="question mr-1"
                                data-tooltip-id="plotly-chart"
                                data-place="left"
                                data-tooltip-content={formatMessage(messages.indicativeTokenValueITVTooltipContent)}
                            >
                                ?
                            </div>
                            <span className="text-14 mr-3 another-grey-color">ITV </span>{" "}
                            <span className="text-16 fw-700 nav">
                                <CustomFormatNumber value={display.ITV} symbol="€" fixed={2} />
                            </span>
                        </div>
                        <div className="text-right mb-1 d-flex justify-end" style={{ lineHeight: 1 }}>
                            <span className="text-10 gain-price d-flex">
                                {/* <CustomFormatNumber value={((display.ITV || 1) - 1.00)} symbol="€" fixed={2} /> */}
                                <PercentValueCom gainPrice={(display.ITV || 1) - 1.0} />
                            </span>{" "}
                            <span className="text-10 percentage ml-1">
                                {/* <CustomFormatNumber value={((display.ITV || 1) - 1.00) * 100} symbol="%" fixed={0} /> */}
                                <PercentValueCom gainPrice={((display.ITV || 1) - 1.0) * 100} fixed={0} symbol="%" />
                            </span>
                        </div>
                    </div>
                </div>
                <div className="w-100 graph-block" ref={gbRef}>
                    <Plot
                        data={[ITV, TMP]}
                        layout={{
                            width: innerWidth,
                            height: innerHeight,
                            showlegend: false,
                            hoverlabel: {
                                font: { color: "#fff" },
                            },
                            margin: {
                                l: 40,
                                r: 10,
                                b: 60,
                                t: 60,
                                pad: 10,
                            },
                            xaxis: {
                                rangeselector: selectorOptions,
                                visible: !!(data.ITV.length || data.TMP.length),
                                type: "date",
                                showgrid: false,
                                showline: false,
                                showticklabels: true,
                                zeroline: true,
                                autorange: true,
                                tickformatstops: [
                                    {
                                        dtickrange: [null, 1000],
                                        value: "%H:%M:%S.%L ms",
                                    },
                                    {
                                        dtickrange: [1000, 60000],
                                        value: "%H:%M:%S s",
                                    },
                                    {
                                        dtickrange: [60000, 3600000],
                                        value: "%H:%M m",
                                    },
                                    {
                                        dtickrange: [3600000, 86400000],
                                        value: "%e %b %y",
                                    },
                                    {
                                        dtickrange: [86400000, 604800000],
                                        value: "%e %b %y",
                                    },
                                    {
                                        dtickrange: [604800000, "M1"],
                                        value: "%e %b %y",
                                    },
                                    {
                                        dtickrange: ["M1", "M12"],
                                        value: "%b %y",
                                    },
                                    {
                                        dtickrange: ["M12", null],
                                        value: "%Y Y",
                                    },
                                ],
                            },
                            yaxis: {
                                visible: !!(data.ITV.length || data.TMP.length),
                                type: "linear",
                                zeroline: true,
                                showgrid: true,
                                showline: false,
                                autorange: false,
                                range: [yRange[0], yRange[1]],
                                tickmode: "auto",
                                nticks: 7,
                                tickformat: ".2f",
                                fixedrange: true,
                            },
                            annotations: [
                                {
                                    visible: !(data.ITV.length || data.TMP.length),
                                    text: formatMessage(messages.noDataFound),
                                    xref: "paper",
                                    yref: "paper",
                                    showarrow: false,
                                    font: {
                                        size: 28,
                                    },
                                },
                            ],
                        }}
                        config={{
                            locale: locale,
                            showTips: false,
                            displayModeBar: true,
                            responsive: true,
                            modeBarButtonsToRemove: [
                                "toImage",
                                "hoverCompareCartesian",
                                "hoverClosestCartesian",
                                "toggleSpikelines",
                                "resetScale2d",
                                "autoScale2d",
                                "select2d",
                                "lasso2d",
                            ],
                            displaylogo: false,
                        }}
                        onClick={(data) => {
                            const infotext = data.points.map(function (d) {
                                return { name: d.data.name, date: d.x, value: d.y };
                            });
                            setDisplay({
                                itvDate:
                                    infotext[0]?.name === "ITV"
                                        ? infotext[0]?.date
                                        : infotext[1]?.name === "ITV"
                                          ? infotext[1]?.date
                                          : display.itvDate,
                                tmpDate:
                                    infotext[1]?.name === "TMP"
                                        ? infotext[1]?.date
                                        : infotext[0]?.name === "TMP"
                                          ? infotext[0]?.date
                                          : display.tmpDate,
                                ITV:
                                    infotext[0]?.name === "ITV"
                                        ? infotext[0]?.value
                                        : infotext[1]?.name === "ITV"
                                          ? infotext[1]?.value
                                          : display.ITV,
                                TMP:
                                    infotext[1]?.name === "TMP"
                                        ? infotext[1]?.value
                                        : infotext[0]?.name === "TMP"
                                          ? infotext[0]?.value
                                          : display.TMP,
                            });
                        }}
                    />
                </div>
            </div>
            <ToolTip id="plotly-chart" />
        </div>
    );
};

export default PerformancePlotlyChart;
